import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { Clock, Lock, Medal, Phone, Rocket, User, Users } from "lucide-react";
import { MdEmail } from "react-icons/md";
import { FcGoogle } from "react-icons/fc";
import { SiFacebook } from "react-icons/si";
import { API_BASE_URL } from "@/utils/api";

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isAdminMode, setIsAdminMode] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const togglePassword = () => setShowPassword(!showPassword);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- FUNGSI REGISTER MANUAL (DIPERBAIKI) ---
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreeTerms) {
      alert("Harap setujui Syarat dan Ketentuan.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
      };

      // PERBAIKAN DISINI: URL diubah ke /api/auth/register
      const response = await fetch(
        `${API_BASE_URL}/auth/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (response.ok) {
        // Cek status HTTP 200/201
        alert(
          data.message ||
            "Registrasi Berhasil! Silakan cek email Anda untuk Voucher.",
        );
        navigate("/auth/Login"); // Arahkan ke halaman login
      } else {
        alert(data.message || "Registrasi Gagal");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Gagal menghubungi server. Pastikan backend menyala.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- GOOGLE LOGIN ---
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const userInfoRes = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          },
        );
        const googleUser = await userInfoRes.json();

        // URL Google sudah benar (/api/auth/google)
        const response = await fetch(
          `${API_BASE_URL}/auth/google`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: googleUser.email,
              name: googleUser.name,
              googleId: googleUser.sub,
              photo: googleUser.picture,
              token: tokenResponse.access_token,
            }),
          },
        );
        const data = await response.json();
        if (data.success) {
          localStorage.setItem("user", JSON.stringify(data.user));
          localStorage.setItem("token", data.token); // Simpan token JWT juga
          window.dispatchEvent(new Event("storage"));
          alert(`Selamat datang, ${data.user.name}!`);
          navigate("/");
        } else {
          alert("Gagal Login Google: " + data.message);
        }
      } catch (error) {
        console.error("Error Google Login:", error);
        alert("Terjadi kesalahan koneksi.");
      }
    },
    onError: (error) => console.error("Login Failed:", error),
  });

  // --- FACEBOOK LOGIN ---
  const responseFacebook = async (response: any) => {
    if (response.accessToken) {
      try {
        // URL Facebook sudah benar (/api/auth/facebook)
        const res = await fetch(
          `${API_BASE_URL}/auth/facebook`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              accessToken: response.accessToken,
              userID: response.userID,
              email: response.email,
              name: response.name,
              picture: response.picture?.data?.url,
            }),
          },
        );

        const data = await res.json();

        if (data.success) {
          localStorage.setItem("user", JSON.stringify(data.user));
          localStorage.setItem("token", data.token); // Simpan token JWT
          alert(`Selamat datang, ${data.user.name}!`);
          navigate("/");
        } else {
          alert("Gagal Login Facebook: " + (data.message || "Error server"));
        }
      } catch (error) {
        console.error("Error sending FB data to backend:", error);
        alert("Gagal terhubung ke server backend.");
      }
    }
  };

  return (
    <>
      <style>{`
        :root {
            --primary-red: #B70100;
            --text-dark: #111827;
            --text-muted: #6B7280;
            --border-color: #E5E7EB;
            --glass-bg: rgba(255, 255, 255, 0.12);
            --bg-page: #F3F4F6;
        }

        body {
            background-color: var(--bg-page);
            font-family: 'Poppins', sans-serif;
            margin: 0;
            padding: 0;
        }

        .auth-page {
            min-height: 100vh;
            display: flex;
            align-items: stretch;
            box-sizing: border-box;
        }

        .auth-layout {
            width: 100%;
            display: flex;
            align-items: stretch;
        }

        .left-panel {
            flex: 1;
            background: #B70100;
            padding: 36px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            box-shadow: 0 24px 80px rgba(183, 1, 0, 0.25);
            overflow: hidden;
            position: relative;
        }

        .left-panel::before {
            content: '';
            position: absolute;
            inset: 0;
            background: radial-gradient(circle at top left, rgba(255,255,255,0.18), transparent 25%),
                        radial-gradient(circle at bottom right, rgba(255,255,255,0.12), transparent 20%);
            pointer-events: none;
            opacity: 0.7;
        }

        .left-content {
            position: relative;
            z-index: 1;
            display: grid;
            gap: 24px;
            margin-top: -80px;
            padding: 30px;
        }

        .glass-card {
            width: max-content;
            background: var(--glass-bg);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 32px;
            backdrop-filter: blur(24px);
            -webkit-backdrop-filter: blur(24px);
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
            color: white;
            padding: 32px 28px;
            display: flex;
            flex-direction: column;
            gap: 14px;
        }

        .glass-card.top-card {
            padding: 15px 24px;
            align-items: flex-start;
            justify-content: center;
            margin-top: 0;
        }

        .glass-card .label-pill {
            position: relative;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0.02em;
            color: #FFFFFF;
        }

        /* old absolute dot removed; using inline .pill-dots-wrapper instead */
        .glass-card .label-pill .pill-dots-wrapper {
          display: flex;
          align-items: center;
          gap: 0; /* tightly packed; overlap via negative margins */
          z-index: 3;
        }

        .glass-card .label-pill .pill-dot {
          display: flex;
          width: 30px;
          height: 30px;
          border-radius: 999px;
          background: #FFE9E6;
          border: 2px solid var(--primary-red);
          margin-left: -5px; /* slight overlap to create intersection effect */
          position: relative;
          z-index: 4;
          color: black;
          justify-content: center;
          align-items: center;
        }

        .glass-card .label-pill .pill-dot:first-child {
          margin-left: 0;
        }

        .left-content h2 {
            margin: 0;
            font-size: 2.3rem;
            font-weight: bold;
            color: #FFFFFF;
        }
            
        .login-panel {
            flex: 0.8;
            min-width: 320px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 50px 50px;
            background: #FFF0EE;
        }

        .login-container {
            width: 100%;
            max-width: 520px;
            background: rgba(255, 248, 246, 0.95);
            border-radius: 32px;
            padding: 32px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
            box-sizing: border-box;
        }

        .top-nav { margin-bottom: 20px; }

        .btn-back {
            background: none;
            border: none;
            color: var(--text-dark);
            font-weight: 600;
            font-size: 14px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 0;
        }

        .header-title { text-align: start; margin-bottom: 3rem; }
        .header-title h2 { font-size: 28px; font-weight: 700; color: var(--text-dark); margin: 0 0 8px 0; }
        .header-title p { color: var(--text-muted); font-size: 14px; margin: 0; }

        form { width: 100%; }
        .form-row { display: flex; gap: 16px; }
        .form-row .input-wrapper { flex: 1; }
        .input-wrapper { margin-bottom: 16px; }
        .input-label { display: block; font-size: 14px; font-weight: 600; color: #5F3F3A; }
        .input-icon {width: 16px; height: auto; color: #5F3F3A}
        .input-field {
            width: 100%;
            padding: 14px 16px;
            font-size: 14px;
            border: 1px solid #E9BCB5;
            border-radius: 12px;
            box-sizing: border-box;
            outline: none;
            background: #FCFCFC;
        }
        .input-field:focus { border-color: var(--primary-red); box-shadow: 0 0 0 2px rgba(183, 1, 0, 0.12); }

        .password-container { position: relative; }
        .toggle-pass {
            position: absolute;
            right: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #9CA3AF;
            cursor: pointer;
            font-size: 16px;
        }

        .terms-check { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 16px; }
        .terms-check input { margin-top: 3px; width: 16px; height: 16px; accent-color: var(--primary-red); cursor: pointer;}
        .terms-check label { font-size: 13px; color: var(--text-muted); line-height: 1.5; }

        .btn-create {
            width: 100%;
            background-color: var(--primary-red);
            color: white;
            padding: 14px;
            border: none;
            border-radius: 12px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.2s ease;
            margin-bottom: 16px;
        }
        .btn-create:hover { background-color: #9d0100; }

       .btn-login {
            width: 100%;
            background-color: var(--primary-red);
            color: white; border: none;
            padding: 14px; border-radius: 12px; font-size: 14px; font-weight: 600;
            cursor: pointer; transition: all 0.2s; margin-bottom: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            box-shadow: 0 14px 30px rgba(183, 1, 0, 0.22);
        }
        .admin-mode .btn-login { background-color: var(--admin-dark); }
        
        .btn-login:hover { opacity: 0.9; }
        .btn-login:disabled { opacity: 0.7; cursor: not-allowed; }

        .social-grid { display: flex; flex-direction: column; gap: 15px; width: 100%; }
        .btn-social {
            display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%;
            padding: 12px; background: white; border-radius: 8px; cursor: pointer;
            font-size: 14px; font-weight: 600; transition: all 0.2s; text-decoration: none;
            border: 1px solid var(--border-color);
            color: #111827;
        }
        .btn-social svg { width: 20px; height: 20px; }
        .btn-social.google { border-color: #FF0000; }
        .btn-social.google svg { color: #FF0000; }
        .btn-social.facebook { border-color: #FF0000; }
        .btn-social.facebook svg { color: #1877F2; }
        .btn-social:hover { 
            background-color: #FAFAFA; 
            transform: translateY(-1px);
            box-shadow: 0 2px 4px var(--primary-red);
        }

            .footer-text { text-align: center; font-size: 14px; color: #6B7280; }
            .signup-link { display: flex; gap: 4px; color: var(--primary-red); font-weight: 600; text-decoration: none; margin-left: 5px; font-size: 14px; align-items: center;}
            .signup-link:hover { text-decoration: underline; }

        .divider { height: 1px; background: #E5E7EB; margin: 12px 0; width: 100%; }
        .footer-text { text-align: center; font-size: 13px; color: var(--text-muted); }
        .footer-text a { color: var(--primary-red); font-weight: 600; text-decoration: none;}
        .footer-text a:hover { text-decoration: underline; }

        @media (max-width: 900px) {
            .auth-layout { flex-direction: column; gap: 24px; }
            .left-panel, .login-panel { width: 100%; height: auto; }
            .left-panel { padding: 28px 24px; }
            .left-content { margin-top: 0; }
            .glass-card { padding: 22px 20px; }
            .glass-card.top-card { padding: 10px 18px; }
            .login-panel { padding: 20px 25px; }
            .login-container { max-width: 100%; padding: 28px 24px; }
        }

        @media (max-width: 760px) {
            .form-row { flex-direction: column; }
            .form-row .input-wrapper { min-width: 100%; }
            .input-field { padding: 12px 14px; }
            .btn-create { padding: 12px; font-size: 13px; }
            .header-title h2 { font-size: 24px; }
            .header-title p { font-size: 13px; }
            .glass-card.hero-card { min-height: 140px; }
        }

        @media (max-width: 520px) {
            .left-panel { display: none; }
            .login-panel { padding: 20px; }
            .login-container { border-radius: 24px; padding: 22px 22px; }
            .social-buttons { gap: 10px; }
            .divider { margin: 10px 0; }
            .header-title { margin-bottom: 18px; }
        }
      `}</style>

      <div className="auth-page">
        <div className="auth-layout">
          <aside className="left-panel">
            <div className="left-content">
              <img
                src="/Registrasi/Sign-up Icon 3 - Language.svg"
                alt="icon 1"
                className="w-28 h-32 ml-auto"
              />
              <div className="glass-card top-card">
                <span className="label-pill">
                  <span className="pill-dots-wrapper" aria-hidden="true">
                    <span className="pill-dot">A</span>
                    <span className="pill-dot">B</span>
                    <span className="pill-dot">C</span>
                  </span>
                  Daftar Bersama 5.000+ Siswa
                </span>
              </div>

              <h2>
                Langkah Awal Menjadi{" "}
                <span className="text-[#DAE2FF]">Fasih</span>
              </h2>

              <div className="grid grid-cols-2 gap-y-6 gap-x-0 w-5/6">
                <div className="flex items-center gap-4 text-white font-medium w-max">
                  <div className="flex rounded-lg bg-[#ffffff]/20 text-white p-2">
                    <Clock></Clock>
                  </div>
                  <p>Jadwal Fleksibel</p>
                </div>
                <div className="flex items-center gap-4 text-white font-medium w-max">
                  <div className="flex rounded-lg bg-[#ffffff]/20 text-white p-2">
                    <Medal></Medal>
                  </div>
                  <p>Kurikulum Resmi</p>
                </div>
                <div className="flex items-center gap-4 text-white font-medium w-max">
                  <div className="flex rounded-lg bg-[#ffffff]/20 text-white p-2">
                    <Rocket></Rocket>
                  </div>
                  <p>Hasil Terukur</p>
                </div>
                <div className="flex items-center gap-4 text-white font-medium w-max">
                  <div className="flex rounded-lg bg-[#ffffff]/20 text-white p-2">
                    <Users></Users>
                  </div>
                  <p>Native Tutors</p>
                </div>
              </div>
            </div>

            <div className="img-con flex flex-col pl-20">
              <div className="w-full justify-end items-end flex">
                <img
                  src="/Registrasi/Sign up icon 2 - Toga.svg"
                  alt="icon 3"
                  className="flex w-20 h-20 mr-auto"
                />
              </div>
              <img
                src="/Registrasi/Sign-up Icon 1 - Book.svg"
                alt="icon 2"
                className="flex ml-auto w-24 h-24"
              />
            </div>
          </aside>

          <section className="login-panel">
            <div className="login-container">
              {/* <div className="top-nav">
                <button className="btn-back" onClick={() => navigate(-1)}>
                  <i className="fas fa-arrow-left"></i> Back
                </button>
              </div> */}

              <div className="header-title">
                <h2>Buat Akun Baru</h2>
                <p>
                  Ayo bergabung dengan komunitas pembelajar bahasa terbesar.
                </p>
              </div>

              <form onSubmit={handleRegister}>
                <div className="form-row">
                  <div className="input-wrapper">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="input-icon" />
                      <label className="input-label">First Name</label>
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="First name"
                      className="input-field"
                      required
                    />
                  </div>
                  <div className="input-wrapper">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="input-icon" />
                      <label className="input-label">Last Name</label>
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Last name"
                      className="input-field"
                      required
                    />
                  </div>
                </div>

                <div className="input-wrapper">
                  <div className="flex items-center gap-2 mb-1">
                    <MdEmail className="input-icon" />
                    <label className="input-label">Email Address</label>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Input your email"
                    className="input-field"
                    required
                  />
                </div>

                <div className="input-wrapper">
                  <div className="flex items-center gap-2 mb-1">
                    <Phone className="input-icon" />
                    <label className="input-label">Phone Number</label>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Input your phone number"
                    className="input-field"
                    required
                  />
                </div>

                <div className="input-wrapper">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="input-icon" />
                    <label className="input-label">Password</label>
                  </div>
                  <div className="password-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Input your password"
                      className="input-field"
                      required
                    />
                    <i
                      className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"} toggle-pass`}
                      onClick={togglePassword}
                    ></i>
                  </div>
                </div>

                <div className="terms-check">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                  />
                  <label htmlFor="terms">
                    By continuing, you agree to Fluenesia Terms of Use and
                    Privacy Policy.
                  </label>
                </div>

                <button
                  type="submit"
                  className="btn-create"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating..." : "Create Account"}
                </button>
              </form>

              {!isAdminMode && (
                <div className="social-grid">
                  <button
                    type="button"
                    className="btn-social google"
                    onClick={() => googleLogin()}
                    disabled={isLoading}
                  >
                    <FcGoogle /> Sign In with Google
                  </button>

                  {/* <FacebookLogin
                    appId={import.meta.env.VITE_FACEBOOK_APP_ID || ""}
                    autoLoad={false}
                    fields="name,email,picture"
                    callback={responseFacebook}
                    isDisabled={isLoading}
                    render={(renderProps: any) => (
                      <button
                        type="button"
                        className="btn-social facebook"
                        onClick={renderProps.onClick}
                        disabled={isLoading}
                      >
                        <SiFacebook /> Sign In with Facebook
                      </button>
                    )}
                  /> */}
                </div>
              )}

              <div className="divider"></div>

              <div className="footer-text">
                Already have an account? <Link to="/auth/login">Log in</Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Register;
